import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { AuthUser, Tenant } from "../types";
import {
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  switchTenant as apiSwitchTenant,
} from "../authApi";

interface AuthContextValue {
  user: AuthUser | null;
  tenants: Tenant[];
  activeTenant: Tenant | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (slug: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [activeTenant, setActiveTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMe()
      .then((res) => {
        setUser(res.user);
        setTenants(res.tenants);
        setActiveTenant(res.active_tenant);
      })
      .catch(() => {
        // Not logged in — that's fine
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiLogin(email, password);
    setUser(res.user);
    setTenants(res.tenants);
    setActiveTenant(res.active_tenant);
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
    setTenants([]);
    setActiveTenant(null);
  }, []);

  const switchTenant = useCallback(async (slug: string) => {
    const res = await apiSwitchTenant(slug);
    setActiveTenant(res.active_tenant);
    // Reload so all views re-fetch data for the new tenant
    window.location.reload();
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, tenants, activeTenant, loading, login, logout, switchTenant }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
